// Vertical dots beside the stacked product gallery on desktop, Montblanc style: one dot per row of pictures,
// the row in view is filled, a click scrolls to that row. Long galleries show a sliding window of dots.
// Rendered by sections/main-product.liquid ("Show picture dots beside the gallery").
if (!customElements.get('gallery-dots')) {
  const VISIBLE_DOTS = 9;
  const desktop = window.matchMedia('(min-width: 990px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  customElements.define(
    'gallery-dots',
    class GalleryDots extends HTMLElement {
      constructor() {
        super();
        this.update = this.update.bind(this);
        this.onScroll = this.onScroll.bind(this);
      }

      connectedCallback() {
        const gallery = document.getElementById(this.dataset.gallery);
        this.list = gallery && gallery.querySelector('.product__media-list');
        this.track = this.querySelector('.gallery-dots__track');
        if (!this.list || !this.track) return;
        this.dots = [];
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.list);
        desktop.addEventListener('change', this.update);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.update();
      }

      disconnectedCallback() {
        if (!this.resizeObserver) return;
        this.resizeObserver.disconnect();
        desktop.removeEventListener('change', this.update);
        window.removeEventListener('scroll', this.onScroll);
      }

      onScroll() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.highlight();
        });
      }

      // Groups the visible pictures into rows by their top edge.
      update() {
        const rows = [];
        if (desktop.matches) {
          this.list.querySelectorAll(':scope > .product__media-item').forEach((item) => {
            if (!item.getClientRects().length) return;
            const top = item.getBoundingClientRect().top;
            const row = rows[rows.length - 1];
            if (row && Math.abs(row.top - top) < 2) row.items.push(item);
            else rows.push({ top, items: [item] });
          });
        }
        this.rows = rows;
        this.hidden = rows.length < 2;
        if (this.hidden) return;
        if (this.dots.length !== rows.length) this.build();
        this.highlight();
      }

      build() {
        const visible = Math.min(this.rows.length, VISIBLE_DOTS);
        this.style.setProperty('--dots-visible', visible);
        this.track.replaceChildren();
        this.current = -1;
        let picture = 1;
        this.dots = this.rows.map((row, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'gallery-dots__dot';
          dot.setAttribute('aria-label', this.dataset.label.replace('__index__', picture));
          dot.addEventListener('click', () => this.scrollToRow(index));
          picture += row.items.length;
          this.track.appendChild(dot);
          return dot;
        });
      }

      // The current row is the top-most one still showing under the header (where a dot click puts its row).
      highlight() {
        if (this.hidden || !this.rows) return;
        const header = document.querySelector('.section-header');
        const line = Math.max(0, header ? header.getBoundingClientRect().bottom : 0) + 40;
        let current = this.rows.length - 1;
        for (let index = 0; index < this.rows.length; index++) {
          const bottom = Math.max(...this.rows[index].items.map((item) => item.getBoundingClientRect().bottom));
          if (bottom > line) {
            current = index;
            break;
          }
        }
        if (current === this.current) return;
        this.current = current;

        const count = this.dots.length;
        const visible = Math.min(count, VISIBLE_DOTS);
        const first = Math.min(Math.max(current - Math.floor(visible / 2), 0), count - visible);
        const last = first + visible - 1;
        this.track.style.setProperty('--dots-offset', first);
        this.dots.forEach((dot, index) => {
          dot.tabIndex = index >= first && index <= last ? 0 : -1;
          dot.classList.toggle('is-active', index === current);
          if (index === current) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
          // The edge dots shrink when more rows lie beyond them.
          let size = '';
          if ((index === first && first > 0) || (index === last && last < count - 1)) size = 'small';
          else if ((index === first + 1 && first > 0) || (index === last - 1 && last < count - 1)) size = 'medium';
          dot.dataset.size = size;
        });
      }

      scrollToRow(index) {
        const top = this.rows[index].items[0].getBoundingClientRect().top + window.scrollY;
        // The header slides back in when the page scrolls up; keep the row clear of it.
        const header = document.querySelector('.section-header');
        const offset = top < window.scrollY && header ? header.offsetHeight + 16 : 16;
        window.scrollTo({ top: Math.max(0, top - offset), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
    }
  );
}
